import {useCallback} from "react";

import {GuideStateApi} from "@khan/guide-state";
import type {NotificationParams, SendGuideItemOptions} from "@khan/guide-types";

import {useSendGuideItem} from "../internal/use-send-guide-item.ts";

export type SendGuideCommandOptions = SendGuideItemOptions;

export type EssayTypePromptVariable =
    | "explanatory / expository"
    | "literary analysis"
    | "persuasive / argumentative"
    | "organized and well-supported"; // For legacy essays

interface SendGuideCommandFn {
    /**
     * Send the request-essay-feedback command.
     */
    (
        name: "request-essay-feedback",
        params: {
            essay: string;
            essayAssignment: string;
            studentGrade: string;
        },
        options: {
            onResponseFailed: SendGuideCommandOptions["onResponseFailed"];
        },
    ): Promise<void>;

    /**
     * Send the writing-coach/outlining-to-drafting command
     */
    (
        name: "writing-coach/outlining-to-drafting",
        params: {
            essayInstructions: string;
            essayOutline: string;
            essayType: EssayTypePromptVariable;
            studentGradeLevel: string;
        },
        options: {
            onResponseFailed: SendGuideCommandOptions["onResponseFailed"];
        },
    ): Promise<void>;

    /**
     * Send the writing-coach/drafting-to-revising command
     */
    (
        name: "writing-coach/drafting-to-revising",
        params: {
            essayInstructions: string;
            essayType: EssayTypePromptVariable;
            initialEssay: string;
            studentGradeLevel: string;
        },
        options: {
            onResponseFailed: SendGuideCommandOptions["onResponseFailed"];
        },
    ): Promise<void>;

    /**
     * Send the coediting/request-edit command.
     */
    (
        name: "coediting/request-edit",
        params: {
            prefix: string;
            selection: string;
            suffix: string;
            instruction: string;
        },
    ): Promise<void>;

    /**
     * Send the coediting/request-insert command.
     */
    (
        name: "coediting/request-insert",
        params: {
            prefix: string;
            selection: string;
            suffix: string;
            instruction: string;
        },
    ): Promise<void>;

    /**
     * Send the associate_conversation_test_case command.
     */
    (
        name: "associate_conversation_test_case",
        params: {
            testCaseId: string;
        },
    ): Promise<void>;

    /**
     * Send the set_conversation_test_case_name command.
     */
    (
        name: "set_conversation_test_case_name",
        params: {
            testCaseId: string;
            name: string;
        },
    ): Promise<void>;

    /**
     * Send the set_conversation_test_case_success command.
     */
    (
        name: "set_conversation_test_case_success",
        params: {
            testCaseId: string;
            success: "true" | "false";
        },
    ): Promise<void>;

    /**
     * Send the "notification/dispatch-request" event
     */
    (
        name: "notification/dispatch-request",
        params: NotificationParams & {
            navigationId: string;
        },
    ): Promise<void>;

    (
        name: "prototype_next_assessment_item",
        params: {
            remainingQuestionCount: string;
        },
    ): Promise<void>;

    // Add other overloads here as needed. For example:
    // (
    //     name: "some-other-command",
    //     params: {a: number, b: string},
    // ): Promise<void>;
}

/**
 * A hook to send commands to the ai guide.
 *
 * @returns A function to send commands to the ai guide. The command to send is
 * defined by an object containing the command name and parameters for that
 * command.
 */
export const useSendGuideCommand = (): SendGuideCommandFn => {
    const sendGuideItem = useSendGuideItem();

    const sendCommand: SendGuideCommandFn = useCallback(
        (
            name: string,
            params?: Record<any, any>,
            options?: SendGuideCommandOptions,
        ) => {
            GuideStateApi.set("isLoading", true);

            return sendGuideItem(
                "command",
                {
                    commandObject: {
                        name: name,
                        params: params ?? {},
                    },
                },
                options,
            ).finally(() => {
                GuideStateApi.set("isLoading", false);
            });
        },
        [sendGuideItem],
    );
    return sendCommand;
};
